import { Add } from '@mui/icons-material'
import { Box, Button, Drawer, IconButton, Typography } from '@mui/material'
import React from 'react'

interface EditorSidebarProps {
  bookDir: 'ltr' | 'rtl'
  isAuthor: boolean
  isMobile: boolean
  sidebarOpen: boolean
  menuButtonSx: Record<string, unknown>
  onSidebarClose: () => void
  onCreateChapter: () => void
  onOpenBookSettings: () => void
  /** Optional block above the chapter list (e.g. ^-marked chapter sections). */
  renderChapterSectionsToc?: () => React.ReactNode
  renderChapterList: () => React.ReactNode
  /**
   * When true (public reader with at least one section), show only the sections list — no standard chapter list.
   */
}

function SidebarContent({
  bookDir,
  isAuthor,
  isMobile,
  menuButtonSx,
  onCreateChapter,
  onOpenBookSettings,
  renderChapterSectionsToc,
  renderChapterList,
}: Omit<EditorSidebarProps, 'sidebarOpen' | 'onSidebarClose'>) {
  const chaptersHeaderRow = isAuthor ? (
    <Box
      sx={{
        pl: 1,
        pr: 1,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid var(--border-subtle)',
        flexShrink: 0,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          flex: 1,
          fontWeight: 600,
          color: 'grey.600',
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Source Files
      </Typography>
      <IconButton
        size="small"
        onClick={onCreateChapter}
        sx={{ color: 'grey.600', '&:hover': { bgcolor: 'grey.100', color: 'grey.800' } }}
      >
        <Add fontSize="small" />
      </IconButton>
    </Box>
  ) : (
    <Box
      sx={{
        pl: 1,
        pr: 0.5,
        py: 1,
        borderBottom: '1px solid var(--border-subtle)',
        flexShrink: 0,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: 'grey.600',
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Source Files
      </Typography>
    </Box>
  )

  const hasChapterSections = Boolean(renderChapterSectionsToc)
  const isRtl = bookDir === 'rtl'
  const scrollFlipSx = isRtl ? { direction: 'ltr' as const } : undefined
  const innerRtlSx = isRtl ? { direction: 'rtl' as const } : undefined

  return (
    <Box
      dir={bookDir}
      sx={{
        width: 280,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'var(--sidebar-bg)',
        overflow: 'hidden',
        height: '100%',
        ...(isMobile ? {} : { borderRight: '1px solid', borderColor: 'var(--border-subtle)' }),
      }}
    >
      {hasChapterSections ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: '1 1 0',
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 1,
              ...scrollFlipSx,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--border-subtle)',
                borderRadius: 2,
              },
            }}
          >
            <Box sx={innerRtlSx}>{renderChapterSectionsToc!()}</Box>
          </Box>
          <Box sx={{ borderTop: '1px solid var(--border-subtle)', flexShrink: 0 }}>{chaptersHeaderRow}</Box>
          <Box
            sx={{
              flex: '1 1 0',
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 1,
              ...scrollFlipSx,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--border-subtle)',
                borderRadius: 2,
              },
            }}
          >
            <Box sx={innerRtlSx}>{renderChapterList()}</Box>
          </Box>
        </Box>
      ) : (
        <>
          {chaptersHeaderRow}
          <Box sx={{ p: 1, flex: 1, minHeight: 0, overflowY: 'auto', ...scrollFlipSx }}>
            <Box sx={innerRtlSx}>{renderChapterList()}</Box>
          </Box>
        </>
      )}

      {isMobile && isAuthor && (
        <Box sx={{ p: 1, borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 1 }}>
          <Button size="small" fullWidth onClick={onOpenBookSettings} sx={menuButtonSx}>
            Book Settings
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default function EditorSidebar(props: EditorSidebarProps) {
  const content = (
    <SidebarContent
      bookDir={props.bookDir}
      isAuthor={props.isAuthor}
      isMobile={props.isMobile}
      menuButtonSx={props.menuButtonSx}
      onCreateChapter={props.onCreateChapter}
      onOpenBookSettings={props.onOpenBookSettings}
      renderChapterSectionsToc={props.renderChapterSectionsToc}
      renderChapterList={props.renderChapterList}
    />
  )

  if (props.isMobile) {
    return (
      <Drawer
        anchor={props.bookDir === 'rtl' ? 'right' : 'left'}
        open={props.sidebarOpen}
        onClose={props.onSidebarClose}
      >
        {content}
      </Drawer>
    )
  }

  return content
}
